import React, { ReactElement, useLayoutEffect } from 'react';
import { Switch, Route, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { AsyncComponent } from '../components';
import Sidebar from 'src/components/Sidebar';
import Spinner from 'src/components/Spinner';
import AcceptTosModal from 'src/pods/user/modals/AcceptTosModal';

import { authRoutes } from '../routes/routes';
import { authService } from '../services/AuthService';
import {
    getPractitionerSuccess,
    getUser,
    setPractitionerLanguages,
    setPractitionerSpecializations,
} from '../pods/user/state/actions';
import Routes, { isPublicRoute } from 'src/enums/routes';

import { RootState } from '../store';
import config from '../config';

import theme from 'src/styles/theme';
import { setOnboardingToComplete } from 'src/pods/onboarding/state/actions';
import AcceptPractitionerTosModal from 'src/pods/user/modals/AcceptPractitionersTosModal';
import { setDisplaySessionSidebar } from 'src/pods/sessions/state/actions';
import UserApiCalls from 'src/pods/user/api/ApiCalls';

const LayoutContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: auto;
    min-height: calc(100vh - 50px);
    box-sizing: border-box;
    background-color: ${theme.constantStyles.colorStyles.white};
`;

const LoggedInLayout = (props: any): ReactElement => {
    const dispatch = useDispatch();
    const history = useHistory();
    const user = useSelector((state: RootState) => state.userReducer.user);
    const practitioner = useSelector((state: RootState) => state.practitionerReducer.practitioner);

    const logout = async () => {
        await authService.logout();
    };

    const loadInitialData = () => {
        dispatch(getUser());
        dispatch(setOnboardingToComplete(true));
        dispatch(setDisplaySessionSidebar(false));
    };

    const onScreenLoad = async () => {
        try {
            if (authService.isAuthenticated()) {
                if (practitioner) {
                    loadInitialData();
                } else {
                    const response = await UserApiCalls.getPractitioner();
                    if (response.data) {
                        dispatch(getPractitionerSuccess(response.data));
                        dispatch(setPractitionerLanguages(response.data.languages ?? []));
                        dispatch(setPractitionerSpecializations(response.data.specializations ?? []));
                        loadInitialData();
                    } else {
                        history.push(Routes.ONBOARDING);
                    }
                }
            } else {
                if (!isPublicRoute(props.match.path)) {
                    return window.location.assign(
                        `${config.SSO_APP_URL}/login?redirectURI=${config.PRACTITIONERS_APP_URL}`,
                    );
                }
            }
        } catch (error) {
            history.push(Routes.ONBOARDING);
        }
    };

    useLayoutEffect(() => {
        onScreenLoad();
        // eslint-disable-next-line
    }, []);

    return (
        <div className="app">
            <Sidebar onLogoutClick={logout} />
            <main className="main">
                {practitioner && !practitioner.tosAccepted ? <AcceptPractitionerTosModal /> : null}
                {user && !user.tosAccepted ? <AcceptTosModal /> : null}
                <LayoutContentWrapper>
                    <Spinner />
                    <Switch>
                        {authRoutes.map((r) => {
                            if (r.withSidebar && !r.disabled) {
                                return (
                                    <Route
                                        key={r.name}
                                        exact
                                        path={r.path}
                                        {...props}
                                        component={AsyncComponent(r.component)}
                                    />
                                );
                            } else {
                                return null;
                            }
                        })}
                    </Switch>
                </LayoutContentWrapper>
            </main>
            {user && !user.tosAccepted ? <AcceptTosModal /> : null}
        </div>
    );
};

export default LoggedInLayout;
